class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = new Map();
    this.sequence = 0;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.welcome = this.querySelector('[data-quick-welcome]');
    this.footer = this.querySelector('[data-quick-footer]');
    this.viewAll = this.querySelector('[data-quick-view-all]');
    this.viewAllText = this.querySelector('[data-quick-view-all-text]');
    this.status = this.querySelector('[data-quick-status]');
    this.scrollCue = this.querySelector('[data-quick-scroll-cue]');
    this.scrollCueDismissed = false;
    this.predictiveSearchResults.addEventListener('scroll', () => {
      if (this.predictiveSearchResults.scrollTop > 2) this.scrollCueDismissed = true;
      this.updateScrollCue();
    }, {passive:true});
    this.predictiveSearchResults.addEventListener('touchstart', event => {
      this.scrollTouch = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null;
    }, {passive:true});
    this.predictiveSearchResults.addEventListener('touchmove', event => {
      if (!this.scrollTouch || event.touches.length !== 1) return;
      const dx = Math.abs(event.touches[0].clientX - this.scrollTouch.x);
      const dy = Math.abs(event.touches[0].clientY - this.scrollTouch.y);
      if (dy > 6 && dy > dx) this.dismissKeyboardForScroll();
    }, {passive:true});
    for (const event of ['touchend','touchcancel']) {
      this.predictiveSearchResults.addEventListener(event, () => { this.scrollTouch = null; }, {passive:true});
    }
    this.predictiveSearchResults.addEventListener('wheel', event => {
      if (Math.abs(event.deltaY) > 2 && Math.abs(event.deltaY) > Math.abs(event.deltaX)) this.dismissKeyboardForScroll();
    }, {passive:true});
    this.scrollObserver = new ResizeObserver(() => this.updateScrollCue());
    this.input.addEventListener('input', () => {
      this.abortController?.abort();
      this.sequence++;
      this.pendingQuery = '';
      this.clearSelection();
      this.syncQuery();
      this.predictiveSearchResults.hidden = true;
      this.updateScrollCue();
      this.input.setAttribute('aria-expanded', 'false');
      this.notify(this.getQuery() ? this.dataset.loadingText : '', true);
    });
    this.input.addEventListener('focus', () => this.onFocus());
    this.input.addEventListener('keydown', event => this.onKeydown(event));
    this.input.form.addEventListener('submit', event => { if (!this.getQuery()) event.preventDefault(); });
  }
  connectedCallback() {
    this.scrollObserver.observe(this.predictiveSearchResults);
  }
  disconnectedCallback() {
    this.abortController?.abort();
    this.scrollObserver.disconnect();
  }
  dismissKeyboardForScroll() {
    if (document.activeElement !== this.input || !this.closest('dialog')?.open || !window.matchMedia('(max-width:767px)').matches) return;
    if (this.predictiveSearchResults.scrollHeight <= this.predictiveSearchResults.clientHeight + 2) return;
    this.scrollCueDismissed = true;
    this.input.blur();
    this.updateScrollCue();
  }
  updateScrollCue() {
    if (!this.scrollCue) return;
    const results = this.predictiveSearchResults;
    const dialog = this.closest('dialog');
    const more = results.scrollHeight - results.clientHeight - Math.max(0, results.scrollTop) > 8;
    this.scrollCue.hidden = this.scrollCueDismissed || !(dialog?.open && dialog.classList.contains('is-compact') && window.matchMedia('(max-width:767px)').matches && !results.hidden && results.clientHeight > 0 && more);
  }
  getQuery() {
    return this.input.value.trim();
  }
  syncQuery() {
    const query = this.getQuery();
    this.resetButton.classList.toggle('hidden', !query);
    this.welcome.hidden = Boolean(query);
    this.footer.hidden = !query;
    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    this.viewAll.href = url.href;
    this.viewAllText.textContent = this.dataset.viewAll.replace('__query__', query);
  }
  onChange() {
    this.syncQuery();
    if (!this.getQuery()) return this.clearResults();
    if (!this.closest('dialog')?.open) return;
    this.getSearchResults(this.getQuery());
  }
  onFocus() {
    this.syncQuery();
    if (this.getQuery()) this.getSearchResults(this.getQuery());
  }
  onFormReset(event) {
    event.preventDefault();
    this.abortController?.abort();
    this.sequence++;
    this.pendingQuery = '';
    this.input.value = '';
    this.syncQuery();
    this.clearResults();
    this.input.focus();
  }
  clearSelection() {
    this.querySelectorAll('[aria-selected=true]').forEach(item => item.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }
  clearResults() {
    this.clearSelection();
    this.predictiveSearchResults.replaceChildren();
    this.predictiveSearchResults.hidden = true;
    this.updateScrollCue();
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.notify('');
  }
  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Enter') {
      const selected = this.querySelector('[role=option][aria-selected=true] a');
      if (selected) { event.preventDefault(); selected.click(); }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = [...this.querySelectorAll('[role=option]')].filter(option => option.getClientRects().length);
    if (!options.length) return;
    event.preventDefault();
    const active = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
    const index = event.key === 'ArrowDown' ? (active + 1) % options.length : (active <= 0 ? options.length - 1 : active - 1);
    this.clearSelection();
    options[index].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', options[index].id);
    options[index].scrollIntoView({block:'nearest'});
  }
  notify(message, visible = false) {
    this.status.textContent = message;
    this.status.classList.toggle('is-visible', visible && Boolean(message));
  }
  async getSearchResults(query) {
    if (this.pendingQuery === query || !this.closest('dialog')?.open) return;
    const key = query.toLowerCase();
    if (this.cachedResults.has(key)) {
      this.renderSearchResults(this.cachedResults.get(key), query);
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    const version = ++this.sequence;
    this.pendingQuery = query;
    this.setAttribute('loading', 'true');
    this.notify(this.dataset.loadingText, true);
    const params = new URLSearchParams({q:query,section_id:'predictive-search','resources[type]':'product,collection,query','resources[limit_scope]':'each','resources[limit]':'10','resources[options][unavailable_products]':'last'});
    try {
      const response = await fetch(`${window.routes.predictive_search_url}?${params}`, {signal:this.abortController.signal});
      if (!response.ok) throw new Error('Predictive search unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Predictive search results missing');
      if (version !== this.sequence || this.getQuery() !== query || !this.closest('dialog')?.open) return;
      this.cachedResults.set(key, section.innerHTML);
      if (this.cachedResults.size > 40) this.cachedResults.delete(this.cachedResults.keys().next().value);
      this.renderSearchResults(section.innerHTML, query);
    } catch (error) {
      if (error.name !== 'AbortError' && version === this.sequence) {
        this.predictiveSearchResults.hidden = true;
        this.updateScrollCue();
        this.input.setAttribute('aria-expanded', 'false');
        this.notify(this.dataset.errorText, true);
      }
    } finally {
      if (version === this.sequence) { this.pendingQuery = ''; this.removeAttribute('loading'); }
    }
  }
  renderSearchResults(markup, query) {
    this.predictiveSearchResults.innerHTML = markup;
    const groups = new Map();
    const normalizedQuery = query.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ');
    const mentionsSize = size => size && (` ${normalizedQuery} `).includes(` ${size.replace(/-/g, ' ')} `);
    const score = item => Number(item.dataset.preference) + (mentionsSize(item.dataset.size) ? 100 : 0);
    for (const item of this.querySelectorAll('[data-quick-product]')) {
      const key = item.dataset.group;
      if (!groups.has(key)) groups.set(key, {chosen:item,items:[],sizes:new Set()});
      const group = groups.get(key);
      group.items.push(item);
      if (item.dataset.size) group.sizes.add(item.dataset.size);
      if (score(item) > score(group.chosen)) group.chosen = item;
    }
    let count = 0;
    const list = this.querySelector('.blissy-quick-search__product-list');
    for (const group of groups.values()) {
      group.items.forEach(item => item.remove());
      if (count++ >= 6) continue;
      const card = group.chosen;
      const size = card.querySelector('[data-quick-size]');
      if (size && group.sizes.size > 1 && !mentionsSize(card.dataset.size)) size.textContent = this.dataset.moreSizes.replace('__size__', size.textContent);
      list.append(card);
      this.updatePrice(card);
    }
    this.predictiveSearchResults.hidden = false;
    this.predictiveSearchResults.scrollTop = 0;
    this.scrollObserver.disconnect();
    this.scrollObserver.observe(this.predictiveSearchResults);
    const content = this.predictiveSearchResults.querySelector('#predictive-search-results');
    if (content) this.scrollObserver.observe(content);
    this.updateScrollCue();
    this.removeAttribute('loading');
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.clearSelection();
    this.notify(this.dataset.readyText);
  }
  updatePrice(card) {
    const amount = card.querySelector('[data-quick-price]');
    if (!amount) return;
    const price = Number(card.dataset.price);
    const compare = Math.max(Number(card.dataset.compare) || 0, price);
    const percent = Math.min(100, Math.max(0, Number(window.discount_percent) || 0));
    const finalPrice = Math.round(price * (1 - percent / 100));
    if (typeof window.Shopify?.formatMoney !== 'function') return;
    amount.textContent = window.Shopify.formatMoney(finalPrice);
    const compareElement = card.querySelector('[data-quick-compare]');
    compareElement.textContent = window.Shopify.formatMoney(compare);
    compareElement.hidden = compare <= finalPrice;
    card.querySelector('[data-compare-label]').hidden = compare <= finalPrice;
    if (compare > finalPrice) card.querySelector('[data-price-label]').textContent = this.dataset.saleLabel;
  }
  close() {
    if (this.scrollCue) this.scrollCue.hidden = true;
    this.scrollCueDismissed = false;
    this.abortController?.abort();
    this.sequence++;
    this.pendingQuery = '';
    this.clearSelection();
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.notify('');
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
